import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { checkOpenAIConnection } from '@/lib/openai';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Check, X, Lock, User, Save } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const OpenAISettings: React.FC = () => {
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [userApiKey, setUserApiKey] = useState<string>('');
  const [isSavingUserKey, setIsSavingUserKey] = useState<boolean>(false);

  useEffect(() => {
    const savedUserKey = localStorage.getItem('user_openai_api_key');
    if (savedUserKey) {
      setUserApiKey(savedUserKey);
    }
    // Initial connection check (will use server key by default or user key if already set)
    handleRefreshStatus(); 
  }, []);

  const handleUserApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserApiKey(e.target.value);
    setIsValid(null); // Reset validation status when key changes
  };

  const handleSaveUserApiKey = async () => {
    if (!userApiKey.trim()) {
      localStorage.removeItem('user_openai_api_key');
      toast({
        title: "User API Key Removed",
        description: "The application will now use the server-configured OpenAI API key.",
      });
      setIsValid(null);
      await handleRefreshStatus(); // Re-check with server key
      return;
    }

    setIsSavingUserKey(true);
    localStorage.setItem('user_openai_api_key', userApiKey);
    toast({
      title: "User API Key Saved",
      description: "Your OpenAI API key has been saved locally and will be used for requests.",
    });
    setIsSavingUserKey(false);
    await handleRefreshStatus(userApiKey); // Validate the newly saved key
  };
  
  const handleRefreshStatus = async (keyToTest?: string) => {
    const currentKey = keyToTest || localStorage.getItem('user_openai_api_key') || undefined;
  
    // ✅ Don't proceed if no key is provided and no key is stored
    if (!currentKey) {
      setIsValid(false);
      setIsValidating(false);
      return; // ❌ No toast, no API call
    }
  
    setIsValidating(true);
    try {
      const isConnected = await checkOpenAIConnection(currentKey);
      setIsValid(isConnected);
  
      toast({
        title: isConnected ? "Connection Successful" : "Connection Failed",
        description: isConnected 
          ? `Successfully connected to OpenAI API using your key.`
          : `Failed to connect to OpenAI API using your key. Please check it.`,
        variant: isConnected ? "default" : "destructive"
      });
    } catch (error) {
      console.error('Error checking OpenAI connection:', error);
      setIsValid(false);
  
      toast({
        title: "Connection Error",
        description: "Failed to check OpenAI API connection.",
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
    }
  };
  

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>OpenAI API Settings</CardTitle>
        <CardDescription>
          Configure OpenAI API access. You can use your own key or the server-provided key.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>Server-Side API Key</AlertTitle>
          <AlertDescription>
            The application has a server-side OpenAI API key. If you do not provide your own key below, the server key will be used.
          </AlertDescription>
        </Alert>

        <div>
          <label htmlFor="userOpenaiApiKey" className="text-sm font-medium mb-1 block flex items-center">
            <User className="h-4 w-4 mr-2 text-primary" />
            Your OpenAI API Key (Optional)
          </label>
          <div className="flex gap-2">
            <Input
              id="userOpenaiApiKey"
              type="password"
              placeholder="sk-..."
              value={userApiKey}
              onChange={handleUserApiKeyChange}
              className="flex-1"
            />
            <Button onClick={handleSaveUserApiKey} disabled={isSavingUserKey || isValidating}>
              <Save className="mr-2 h-4 w-4" />
              {isSavingUserKey ? 'Saving...' : 'Save Key'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            If provided, this key will be used instead of the server key. Stored locally in your browser. Leave blank to use the server key.
          </p>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-muted-foreground">
              Your OpenAI key API Connection Status: 
            </p>
            {isValidating ? (
              <div className="flex items-center text-sm">
                <Loader2 className="animate-spin h-4 w-4 mr-1 text-muted-foreground" />
                <span>Checking...</span>
              </div>
            ) : isValid === null ? (
              <span className="text-sm font-medium">Unknown</span>
            ) : isValid ? (
              <div className="flex items-center text-sm text-green-600">
                <Check className="h-4 w-4 mr-1" />
                <span>Connected</span>
              </div>
            ) : (
              <div className="flex items-center text-sm text-red-600">
                <X className="h-4 w-4 mr-1" />
                <span>Not connected</span>
              </div>
            )}
          </div>
          
          <Button
            onClick={() => handleRefreshStatus(userApiKey || undefined)}
            disabled={isValidating}
            variant="outline"
            className="w-full sm:w-auto"
          >
            {isValidating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking Connection
              </>
            ) : (
              'Re-check Connection Status'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OpenAISettings;